import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, fmtErr } from "@/lib/api";
import { RUN_META, checkpointSchedule } from "@/lib/format";
import { Empty } from "@/components/widgets";
import { PrintBar } from "@/components/PrintBar";
import { CheckpointTime } from "@/components/CheckpointTime";
import { QueryBoundary, UpdatedAt, retryTransient } from "@/lib/queryState";
import { LIVE_REFETCH_MS, useLiveWindow } from "@/lib/marketHours";
import { isLate, isScoringOnly, lateLabel } from "@/lib/checkpointState";
import {
  PageHead, Panel, Badge, Button, Icon, DataStateBadge, DataBanner, DecisionBadge, PathChip, RuleResult,
  StatCell, Delta, RunDiffTable, CheckpointTimeline, fmt, etSyd, timeET, sessionDateLabel, normDecision, useViewport, etAndLocal } from "@/sentinel";

// Lightweight inline markdown: **bold**, _italic_/*italic*, line breaks. Avoids a new dependency.
function renderInline(line, key) {
  const parts = [];
  const re = /(\*\*[^*]+\*\*|_[^_]+_|\*[^*]+\*)/g;
  let last = 0, m, i = 0;
  while ((m = re.exec(line)) !== null) {
    if (m.index > last) parts.push(line.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("**")) parts.push(<strong key={`${key}-${i}`} style={{ color: "var(--text-primary)", fontWeight: 600 }}>{tok.slice(2, -2)}</strong>);
    else parts.push(<em key={`${key}-${i}`}>{tok.slice(1, -1)}</em>);
    last = m.index + tok.length; i += 1;
  }
  if (last < line.length) parts.push(line.slice(last));
  return parts;
}

function NarrativeMd({ md, run }) {
  const lines = (md || "").split("\n");
  return (
    <div className="sn-small" data-testid={`cp-narrative-${run}`}
      style={{ background: "var(--surface-sunken)", color: "var(--text-secondary)", padding: "var(--space-3)", borderRadius: "var(--radius-sm)", overflowWrap: "anywhere", lineHeight: "18px" }}>
      {lines.map((l, idx) => (l.trim() === "" ? <div key={idx} style={{ height: 8 }} /> : <p key={idx} style={{ margin: "0 0 4px" }}>{renderInline(l, idx)}</p>))}
    </div>
  );
}

const sentence = (s) => (s ? String(s).charAt(0) + String(s).slice(1).toLowerCase() : "");
const shortId = (id) => (id ? String(id).slice(0, 8) : "—");
const tsLabel = (ts) => { const v = etSyd(ts); return v === "—" ? ts || "—" : v; };

const FIELD_LABEL = {
  forecast_open: ["Forecast open", "index"], forecast_high: ["Forecast high", "index"], forecast_low: ["Forecast low", "index"],
  close_q10: ["Close q10", "index"], close_q50: ["Close q50", "index"], close_q90: ["Close q90", "index"],
  expected_range: ["Expected range", "pts"], stress_high: ["Stress high", "index"], stress_low: ["Stress low", "index"],
  decision_label: ["Decision", null], confidence: ["Confidence", null],
};
const INDEX_FIELDS = new Set(["forecast_open", "forecast_high", "forecast_low", "close_q10", "close_q50", "close_q90", "stress_high", "stress_low", "expected_range"]);

/** changes_json → RunDiffTable rows. Decision renders as a (muted) badge; index levels as 1dp; confidence as text. */
function diffRows(changes) {
  return changes.map((ch, i) => {
    const [label, kind] = FIELD_LABEL[ch.field] || [ch.field, null];
    const show = (v) => (typeof v === "number" && kind ? fmt(v, kind) : v == null ? "—" : String(v));
    const isDecision = ch.field === "decision_label";
    let why = "—";
    if (ch.delta != null && INDEX_FIELDS.has(ch.field)) why = <Delta value={ch.delta} kind={FIELD_LABEL[ch.field][1]} />;
    else if (ch.delta != null) why = <span className="sn-num">{ch.delta > 0 ? "+" : ch.delta < 0 ? "−" : ""}{Math.abs(ch.delta)}</span>;
    else if (isDecision) why = "Re-evaluated at this run";
    return {
      field: isDecision ? "Decision" : label,
      key: `${ch.field}-${i}`,
      was: isDecision ? (ch.from ? normDecision(ch.from) : "—") : show(ch.from),
      now: isDecision ? normDecision(ch.to) : show(ch.to),
      why,
    };
  });
}

/** The timeline's six nodes, from real checkpoints. The latest sealed original is "current"; earlier ones are frozen. */
function timelineNodes(origs, recalcs, selectedDate) {
  const byRun = new Map(origs.map((c) => [c.run, c]));
  const latest = origs.length ? Math.max(...origs.map((c) => c.run)) : 0;
  return [1, 2, 3, 4, 5, 6].map((n) => {
    const cp = byRun.get(n);
    const sched = checkpointSchedule(selectedDate, cp?.cutoff_et || RUN_META[n].time);
    const sib = recalcs.filter((r) => r.run === n).slice(-1)[0];
    return {
      id: "R" + n,
      time: cp?.cutoff_et || RUN_META[n].time,
      s: !cp ? "pending" : n === latest ? "current" : "frozen",
      decision: cp && !(n === 6 || isScoringOnly(cp)) ? cp.decision_label : null,
      name: (
        <span data-testid={`tl-node-${n}`} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span>{n === 6 || isScoringOnly(cp) ? (cp ? "Post-close review · recorded" : "Post-close review") : sentence(cp?.stage || RUN_META[n].name)}{isLate(cp) ? " · LATE" : ""}</span>
          <span className="sn-faint" data-testid={`tl-time-${n}`}>
            <span className="sn-vh" data-testid={`tl-time-${n}-et`}>{etAndLocal(cp?.cutoff_et || RUN_META[n].time, { day: selectedDate })}</span>
            <span data-testid={`tl-time-${n}-sydney`}>{etAndLocal(cp?.cutoff_et || RUN_META[n].time, { day: selectedDate }) || sched?.sydney || "time unavailable"}</span>
          </span>
        </span>
      ),
      recalc: sib ? { id: `R${n}·RECALC`, time: timeET(sib.timestamp_et, { seconds: false }), reason: "Appended recalculation; the original is untouched" } : null,
    };
  });
}

function SafetyNotice({ c }) {
  const blocks = c.safety_blocks || [];
  const kill = c.operational_status === "KILL SWITCH" || blocks.some((b) => b.code === "KILL");
  const text = `Safety restriction recorded: ${blocks.map((b) => b.code).join(", ") || c.operational_status}.`;
  if (kill) {
    return (
      <DataBanner kind="kill-armed" inline title="KILL SWITCH ARMED at this run" data-testid={`cp-safety-warning-${c.id}`}>
        {text}
      </DataBanner>
    );
  }
  return (
    <div role="alert" className="sn-stack" data-testid={`cp-safety-warning-${c.id}`}
      style={{ gap: "var(--space-1)", padding: "var(--space-2) var(--space-3)", background: "var(--validation-fail-dim)", boxShadow: "inset 2px 0 0 var(--validation-fail)", borderRadius: "var(--radius-sm)", overflowWrap: "anywhere" }}>
      <span className="sn-small" style={{ color: "var(--text-primary)" }}>{text}</span>
      {blocks.map((b, i) => (
        <span key={i} className="sn-row sn-small" style={{ gap: "var(--space-2)", flexWrap: "wrap" }}>
          <RuleResult result="FAIL">FAIL {b.code}</RuleResult>
          {b.reason && <span className="sn-muted">{b.reason}</span>}
        </span>
      ))}
    </div>
  );
}

function CheckpointCard({ c, sibIndex, parent, loud, selectedDate, can, regenId, onRegen, mobile }) {
  const sib = sibIndex != null;
  const [open, setOpen] = useState(false);
  const scoring = isScoringOnly(c);
  const runLabel = sib ? `R${c.run}·RECALC-${sibIndex + 1}` : `R${c.run}`;
  const state = "frozen";
  const contentId = `cp-technical-body-${c.id}`;
  return (
    <article
      className="sn-panel"
      data-testid={`checkpoint-${c.run}${c.recalc_of ? "-recalc" : ""}`}
      aria-label={`${runLabel} · ${sib ? "RECALC sibling" : "FROZEN"}`}
      style={{
        position: "relative",
        padding: "var(--space-4)",
        marginLeft: sib && !mobile ? "var(--space-8)" : 0,
        borderStyle: sib ? "dashed" : "solid",
        borderColor: sib ? "var(--border-control)" : "var(--frozen)",
        boxShadow: sib ? "none" : "inset 0 0 0 2px var(--surface-panel), inset 0 0 0 3px var(--frozen)",
        background: sib ? "var(--surface-panel)" : "var(--frozen-dim)",
      }}
    >
      {sib && !mobile && (
        <span aria-hidden="true" style={{ position: "absolute", left: -20, top: -21, width: 20, height: 42, borderLeft: "1px dashed var(--border-control)", borderBottom: "1px dashed var(--border-control)" }} />
      )}
      <div className="sn-stack" style={{ gap: "var(--space-3)" }}>
        {/* Header: run, stage, seal state · recorded time, decision */}
        <div className="sn-row" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: "var(--space-2)" }}>
          <div className="sn-row" style={{ gap: "var(--space-2)", flexWrap: "wrap", minWidth: 0 }}>
            {!sib && <Icon name="Lock" size={16} style={{ color: "var(--frozen)" }} />}
            <span className="sn-num" style={{ fontWeight: 600, fontSize: 14 }}>{runLabel}</span>
            <span className="sn-small sn-muted">{scoring ? "Post-close review" : sentence(c.stage)}</span>
            {isLate(c) && <Badge icon="Clock" className="sn-prov" title={lateLabel(c)} data-testid={`late-tag-${c.id}`}>LATE · {lateLabel(c).toLowerCase()}</Badge>}
            {c.recalc_of
              ? <DataStateBadge state="RECALC" data-testid={`recalc-tag-${c.id}`} />
              : <DataStateBadge state="FROZEN" data-testid={`frozen-tag-${c.run}`} />}
          </div>
          <div className="sn-row" style={{ gap: "var(--space-2)", flexWrap: "wrap", minWidth: 0 }}>
            <span className="sn-num sn-small sn-faint" style={{ overflowWrap: "anywhere" }} title={c.timestamp_et} data-testid={`cp-timestamp-${c.id}`}>Recorded · {tsLabel(c.timestamp_et)}</span>
            {scoring
              ? <Badge icon="Target" data-testid={`cp-pill-${c.id}`}>No trading decision</Badge>
              : c.decision_label
              ? <DecisionBadge decision={normDecision(c.decision_label)} size="compact" muted={!loud} data-testid={`cp-pill-${c.id}`} />
              : <span className="sn-small sn-faint" data-testid={`cp-pill-${c.id}`}>—</span>}
          </div>
        </div>

        {sib && (
          <p className="sn-small sn-muted" style={{ margin: 0 }}>
            Appended sibling. The original R{c.run}{parent ? <> (<span className="sn-num">{shortId(parent.id)}</span>)</> : null} is untouched.
          </p>
        )}

        <div className="sn-row sn-small" style={{ gap: "var(--space-2)", alignItems: "flex-start" }} data-testid={`cp-scheduled-cutoff-${c.id}`}>
          <span className="sn-faint" style={{ minWidth: 112 }}>Scheduled cutoff</span>
          <CheckpointTime sessionDate={selectedDate} cutoffET={c.cutoff_et} testId={`cp-time-${c.id}`} />
        </div>

        {(c.safety_blocks?.length > 0 || c.operational_status === "KILL SWITCH") && <SafetyNotice c={c} />}

        {/* Sealed forecast values */}
        <div style={{ display: "grid", gridTemplateColumns: mobile ? "repeat(2, minmax(0, 1fr))" : "repeat(6, minmax(0, 1fr))", gap: "var(--space-3)" }}>
          <StatCell size="sm" label="Open" value={c.forecast_open} kind="index" state={state} />
          <StatCell size="sm" label="High" value={c.forecast_high} kind="index" state={state} />
          <StatCell size="sm" label="Low" value={c.forecast_low} kind="index" state={state} />
          <StatCell size="sm" label="Close q50" value={c.close_q50} kind="index" state={state} />
          <StatCell size="sm" label="Confidence" value={c.confidence == null ? "—" : String(c.confidence)} state={state} />
          <div className="sn-stat sn-stat-sm">
            <span className="sn-stat-k">Path</span>
            <span>{c.path_state ? <PathChip path={String(c.path_state).replace(/_/g, " ")} /> : <span className="sn-faint">—</span>}</span>
          </div>
        </div>

        {c.actual_open != null && (
          <div className="sn-small sn-muted">
            Forecast open <span className="sn-num">{fmt(c.original_forecast_open, "index")}</span> vs actual open <span className="sn-num">{fmt(c.actual_open, "index")}</span> · error{" "}
            {c.open_error != null ? <Delta value={c.open_error} kind="index" /> : "—"}
          </div>
        )}

        {c.changes_json?.length > 0 && (
          <div className="sn-stack" style={{ gap: "var(--space-1)" }}>
            <span className="sn-micro">Changes vs previous run</span>
            <div className="sn-table-wrap">
              <RunDiffTable rows={diffRows(c.changes_json)} from={`R${c.run - 1}`} to={runLabel} />
            </div>
          </div>
        )}

        <div className="sn-stack" style={{ gap: "var(--space-1)", overflowWrap: "anywhere" }} data-testid={`cp-novice-summary-${c.id}`}>
          <span className="sn-micro" data-testid={`cp-summary-source-${c.id}`}>
            {c.plain_summary?.source === "xai" ? "In plain English · AI explanation" : "In plain English · saved-rules summary"}
          </span>
          <p style={{ margin: 0, fontSize: 14, lineHeight: "20px", color: "var(--text-primary)" }} data-testid={`cp-summary-text-${c.id}`}>
            {c.plain_summary?.text || "The simple summary is unavailable. The original explanation is preserved below."}
          </p>
        </div>

        <div className="sn-stack" style={{ gap: "var(--space-2)" }} data-testid={`cp-technical-${c.id}`}>
          <div className="sn-row" style={{ gap: "var(--space-2)", flexWrap: "wrap" }}>
            <Button size="sm" variant="ghost" icon={open ? "ChevronDown" : "ChevronRight"} aria-expanded={open} aria-controls={contentId}
              onClick={() => setOpen((o) => !o)} data-testid={`cp-technical-toggle-${c.id}`}>
              {open ? "Hide original technical explanation" : "Show original technical explanation"}
            </Button>
            {can("analyst") && (
              <Button
                size="sm"
                icon={Sparkles}
                loading={regenId === c.id}
                onClick={() => onRegen(c.id)}
                disabled={regenId === c.id || c.plain_summary?.source === "xai"}
                data-testid={`regen-narrative-${c.id}`}
                title="Explain saved facts simply with AI; never replace the original checkpoint"
              >
                {regenId === c.id ? "Simplifying…" : c.plain_summary?.source === "xai" ? "AI explanation saved" : "Simplify with AI"}
              </Button>
            )}
          </div>
          {open && (
            <div id={contentId} data-testid={`cp-technical-content-${c.id}`}>
              <NarrativeMd md={c.narrative_md} run={c.id} />
            </div>
          )}
        </div>

        {/* Developer detail (IDs, versions, which engine wrote the explanation) stays folded away by default. */}
        <details className="sn-small sn-faint" data-testid={`cp-details-${c.id}`}>
          <summary style={{ cursor: "pointer" }}>Technical details</summary>
          <div className="sn-stack" style={{ gap: "var(--space-2)", marginTop: "var(--space-2)" }}>
            <div className="sn-row" style={{ gap: "var(--space-2)", flexWrap: "wrap" }}>
              <Badge icon={c.narrative_source === "xai" ? "MessageSquare" : "FileText"} data-testid={`cp-narrative-source-${c.id}`}>
                {c.narrative_source === "xai" ? "Explanation by XAI · Claude" : "Explanation from template"}
              </Badge>
              {c.t3_active && <Badge icon="Info">T3 ACTIVE</Badge>}
            </div>
            <div className="sn-num" style={{ fontSize: 12, overflowWrap: "anywhere" }} data-testid={`cp-provenance-${c.id}`}>
              ID {shortId(c.id)} · snapshot {c.snapshot_id?.slice(0, 8)} · prompt {c.prompt_version} · thresholds {c.threshold_version} · schema {c.schema_version} · model {c.model_id}
              {sib && <> · parent {shortId(c.recalc_of)}</>}
            </div>
          </div>
        </details>
      </div>
    </article>
  );
}

/** Print-only run table (the Paper audit export): one row per run, RECALC rows indented beneath their original. */
function PrintRunTable({ ledger, latestId }) {
  return (
    <div className="print-only">
      <p className="sn-kicker">Run ledger</p>
      <table className="sn-table">
        <thead><tr><th>Run</th><th>Recorded (ET · local)</th><th>Scheduled cutoff</th><th>Decision</th><th>Path</th><th>Snapshot</th></tr></thead>
        <tbody>
          {ledger.map(({ c, sibIndex }) => (
            <tr key={c.id}>
              <td className="id" style={{ paddingLeft: sibIndex != null ? "var(--space-6)" : undefined }}>
                {sibIndex != null ? `↳ R${c.run}·RECALC-${sibIndex + 1}` : `R${c.run}`}
              </td>
              <td className="sn-num">{tsLabel(c.timestamp_et)}</td>
              <td className="sn-num">{c.cutoff_et ? etAndLocal(c.cutoff_et, { day: c.session_date || selectedDate }) : "—"}</td>
              <td>{isScoringOnly(c) ? "Post-close review" : c.decision_label ? <DecisionBadge decision={normDecision(c.decision_label)} size="compact" muted={c.id !== latestId} /> : "—"}{isLate(c) ? " · LATE" : ""}</td>
              <td>{c.path_state || "—"}</td>
              <td className="id">{shortId(c.snapshot_id)}{c.recalc_of ? ` · parent ${shortId(c.recalc_of)}` : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="sn-small sn-muted" style={{ marginTop: "var(--space-2)" }}>
        Recalculations are appended as siblings; originals are never edited. No order was transmitted by Sentinel.
      </p>
    </div>
  );
}

export default function Checkpoints() {
  const { selectedDate, can } = useAuth();
  const qc = useQueryClient();
  const { mobile } = useViewport();
  const [regenId, setRegenId] = useState(null);
  const win = useLiveWindow(selectedDate);
  const query = useQuery({
    queryKey: ["checkpoints", selectedDate], queryFn: () => api.getCheckpoints(selectedDate), enabled: !!selectedDate,
    refetchInterval: win.polling ? LIVE_REFETCH_MS : false, retry: retryTransient,
  });
  const checkpoints = query.data || [];

  const regen = async (cpId) => {
    setRegenId(cpId);
    try {
      await api.regenerateNarrative(selectedDate, cpId);
      await qc.invalidateQueries({ queryKey: ["checkpoints", selectedDate] });
      toast.success("Simple explanation saved. The original checkpoint is unchanged.");
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || e.message || "Regenerate failed");
    } finally {
      setRegenId(null);
    }
  };

  if (!selectedDate) return <Empty testId="checkpoints-empty">Select a US trading session to see its run schedule.</Empty>;

  const byTime = (a, b) => (a.timestamp_et < b.timestamp_et ? -1 : 1);
  const ordered = [...checkpoints].sort(byTime);
  const origs = checkpoints.filter((c) => !c.recalc_of);
  const recalcs = ordered.filter((c) => c.recalc_of);
  const decisionOrigs = origs.filter((c) => !isScoringOnly(c));
  const latestRun = decisionOrigs.length ? Math.max(...decisionOrigs.map((c) => c.run)) : 0;
  const latest = decisionOrigs.find((c) => c.run === latestRun) || null;

  // Ledger: originals by run, each followed by its RECALC siblings (appended, oldest first).
  const ledger = [];
  const placed = new Set();
  [...origs].sort((a, b) => a.run - b.run || byTime(a, b)).forEach((o) => {
    ledger.push({ c: o });
    recalcs.filter((r) => r.recalc_of === o.id || (!origs.some((x) => x.id === r.recalc_of) && r.run === o.run))
      .forEach((r, i) => { ledger.push({ c: r, sibIndex: i, parent: o }); placed.add(r.id); });
  });
  recalcs.filter((r) => !placed.has(r.id)).forEach((r, i) => ledger.push({ c: r, sibIndex: i, parent: null }));

  return (
    <div className="sn-stack print-page" style={{ gap: "var(--space-5)" }} data-testid="checkpoints-page">
      <PrintBar title="Run log" testId="print-checkpoints-btn" />
      <PageHead
        crumb={["Session desk", <span className="sn-sans-num">{sessionDateLabel(selectedDate)}</span>]}
        title="Runs"
        context={<span className="sn-row" style={{ gap: "var(--space-2)" }}>
          {win.live ? <DataStateBadge state="LIVE" /> : <DataStateBadge state="FROZEN" />}
          <span>{win.live ? "Live" : win.isToday ? "Saved" : "Archived"} · {latest ? <>latest decision R{latest.run} · <span className="sn-num">{tsLabel(latest.timestamp_et)}</span></> : "no decision yet"}</span>
          {win.live && <UpdatedAt at={query.dataUpdatedAt} data-testid="checkpoints-updated-at" />}
        </span>}
      />

      <Panel title="Timeline" icon="GitCommitVertical" className="no-print" data-testid="checkpoint-timeline"
        meta={<span data-testid="checkpoint-timeline-caption">Scheduled cutoffs · New York / Sydney</span>}>
        <div style={{ overflowX: mobile ? "visible" : "auto" }}>
          <div style={{ minWidth: mobile ? 0 : 720 }}>
            <CheckpointTimeline checkpoints={timelineNodes(origs, recalcs, selectedDate)} orientation={mobile ? "vertical" : "horizontal"} loudCurrent={false} />
          </div>
        </div>
      </Panel>

      <QueryBoundary query={query} loading={4} what={`the runs for ${selectedDate}`} errorTestId="checkpoints-error"
        empty={{ icon: "GitCommitVertical", title: "No runs sealed yet", body: `No run has been sealed for ${sessionDateLabel(selectedDate)}. The timeline shows the scheduled times.`, testId: "checkpoints-empty" }}>
        {() => (
          <>
            <PrintRunTable ledger={ledger} latestId={latest?.id} />
            <section className="sn-stack" style={{ gap: "var(--space-3)" }} aria-label="Run ledger">
              <span className="sn-kicker">Run log · {ordered.length} rows · newest last · corrections are appended as RECALC siblings</span>
              {ledger.map(({ c, sibIndex, parent }) => (
                <CheckpointCard key={c.id} c={c} sibIndex={sibIndex} parent={parent} loud={!!latest && c.id === latest.id}
                  selectedDate={selectedDate} can={can} regenId={regenId} onRegen={regen} mobile={mobile} />
              ))}
            </section>
          </>
        )}
      </QueryBoundary>
    </div>
  );
}
